import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { authAPI } from '../services/(adminapi)/authAPI'
import { 
  fetchConfig,
  createOrUpdateConfig,
  clearError,
  clearSuccess
} from '../store/slices/configSlice'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import NotificationModal from '../components/NotificationModal'

const AIConfiguration = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, business } = useSelector((state) => state.auth)

  const [isSigningOut, setIsSigningOut] = useState(false)
  const [formData, setFormData] = useState({
    prompt_templates: {
      system_prompt: '',
      query_reformulation_prompt: '',
      answer_grounding_instruction: ''
    },
    stopwords: [],
    human_chat_keywords: [],
    synonyms: {},
    is_active: true
  })
  const [stopwordsInput, setStopwordsInput] = useState('')
  const [humanChatKeywordsInput, setHumanChatKeywordsInput] = useState('')
  const [synonymsInput, setSynonymsInput] = useState('')
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewConfig, setViewConfig] = useState(null)
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  })

  const { config, loading: isLoading, saving: isSaving, error, success } = useSelector((state) => state.config)

  useEffect(() => {
    dispatch(fetchConfig())
  }, [dispatch])

  // Update form data when config is loaded
  useEffect(() => {
    if (config) {
      setFormData({
        prompt_templates: config.prompt_templates || {
          system_prompt: '',
          query_reformulation_prompt: '',
          answer_grounding_instruction: ''
        },
        stopwords: config.stopwords || [],
        human_chat_keywords: config.human_chat_keywords || [],
        synonyms: config.synonyms || {},
        is_active: config.is_active !== undefined ? config.is_active : true
      })
      setStopwordsInput((config.stopwords || []).join(', '))
      setHumanChatKeywordsInput((config.human_chat_keywords || []).join(', '))
      // Convert synonyms object to string format
      const synonymsStr = Object.entries(config.synonyms || {})
        .map(([key, values]) => `${key}: ${Array.isArray(values) ? values.join(', ') : values}`)
        .join('\n')
      setSynonymsInput(synonymsStr)
    }
  }, [config])

  // Show notifications for errors and success
  useEffect(() => {
    if (error) {
      showNotification('error', 'Error', error)
      dispatch(clearError())
    }
    if (success) {
      showNotification('success', 'Success', success)
      dispatch(clearSuccess())
      // Clear form after successful save
      setFormData({
        prompt_templates: {
          system_prompt: '',
          query_reformulation_prompt: '',
          answer_grounding_instruction: ''
        },
        stopwords: [],
        human_chat_keywords: [],
        synonyms: {},
        is_active: true
      })
      setStopwordsInput('')
      setHumanChatKeywordsInput('')
      setSynonymsInput('')
    }
  }, [error, success, dispatch])

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    })
  }

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }))
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name.startsWith('prompt_templates.')) {
      const field = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        prompt_templates: {
          ...prev.prompt_templates,
          [field]: value
        }
      }))
    } else if (name === 'is_active') {
      setFormData(prev => ({
        ...prev,
        [name]: e.target.checked
      }))
    }
  }

  const handleStopwordsChange = (e) => {
    setStopwordsInput(e.target.value)
    const stopwords = e.target.value
      .split(',')
      .map(word => word.trim())
      .filter(word => word)
    setFormData(prev => ({
      ...prev,
      stopwords
    }))
  }

  const handleHumanChatKeywordsChange = (e) => {
    setHumanChatKeywordsInput(e.target.value)
    const human_chat_keywords = e.target.value
      .split(',')
      .map(word => word.trim())
      .filter(word => word)
    setFormData(prev => ({
      ...prev,
      human_chat_keywords
    }))
  }

  const handleSynonymsChange = (e) => {
    setSynonymsInput(e.target.value)
    const lines = e.target.value.split('\n').filter(line => line.trim())
    const synonyms = {}
    lines.forEach(line => {
      const [key, ...values] = line.split(':')
      if (key && values.length > 0) {
        synonyms[key.trim()] = values.join(':').split(',').map(v => v.trim()).filter(v => v)
      }
    })
    setFormData(prev => ({
      ...prev,
      synonyms
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    dispatch(createOrUpdateConfig(formData))
  }

  const handleViewConfigs = async () => {
    if (config) {
      setViewConfig(config)
      setIsViewModalOpen(true)
    } else {
      showNotification('info', 'Info', 'No configuration found. Please save a configuration first.')
    }
  }

  const closeViewModal = () => {
    setIsViewModalOpen(false)
    setViewConfig(null)
  }

  const handleSignOut = async () => {
    setIsSigningOut(true)
    
    try {
      await dispatch(signOut()).unwrap()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setTimeout(() => {
        navigate('/signin')
        setIsSigningOut(false)
      }, 500)
    }
  }


  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0
  }

  const cardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '24px'
  }

  const cardTitleStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: '0 0 16px 0'
  }

  const formGroupStyle = {
    marginBottom: '24px'
  }

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.text.primary,
    marginBottom: '8px'
  }

  const inputStyle = {
    width: '100%',
    padding: '12px',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: colors.text.primary,
    backgroundColor: colors.background.secondary
  }

  const textareaStyle = {
    ...inputStyle,
    minHeight: '120px',
    resize: 'vertical'
  }

  const checkboxStyle = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    marginRight: '8px'
  }

  const buttonStyle = {
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const loaderOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  }

  const loaderContainerStyle = {
    backgroundColor: colors.background.primary,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    minWidth: '200px'
  }

  const spinnerStyle = {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px auto'
  }

  const loaderTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    margin: 0,
    fontWeight: '500'
  }

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <Sidebar />
        <div style={mainContentStyle}>
          <div style={loaderContainerStyle}>
            <div style={spinnerStyle}></div>
            <p style={loaderTextStyle}>Loading configuration...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <Sidebar />
      
      <div style={mainContentStyle}>
        <PageTopBar
          title="AI Configuration"
          onAiConfig={() => {}}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />

        <form onSubmit={handleSubmit}>
          {/* Prompt Templates */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>Prompt Templates</h2>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>System Prompt</label>
              <textarea
                name="prompt_templates.system_prompt"
                value={formData.prompt_templates.system_prompt}
                onChange={handleInputChange}
                style={textareaStyle}
                placeholder="Enter system prompt..."
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Query Reformulation Prompt</label>
              <textarea
                name="prompt_templates.query_reformulation_prompt"
                value={formData.prompt_templates.query_reformulation_prompt}
                onChange={handleInputChange}
                style={textareaStyle}
                placeholder="Enter query reformulation prompt..."
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Answer Grounding Instruction</label>
              <textarea
                name="prompt_templates.answer_grounding_instruction"
                value={formData.prompt_templates.answer_grounding_instruction}
                onChange={handleInputChange}
                style={textareaStyle}
                placeholder="Enter answer grounding instruction..."
              />
            </div>
          </div>

          {/* Stopwords */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>Stopwords</h2>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Stopwords (comma-separated)
              </label>
              <input
                type="text"
                value={stopwordsInput}
                onChange={handleStopwordsChange}
                style={inputStyle}
                placeholder="word1, word2, word3..."
              />
            </div>
          </div>

          {/* Human chat keywords */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>Human Chat Keywords</h2>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Keywords that trigger handoff to a human agent (comma-separated)
              </label>
              <input
                type="text"
                value={humanChatKeywordsInput}
                onChange={handleHumanChatKeywordsChange}
                style={inputStyle}
                placeholder="agent, representative, speak to someone..."
              />
            </div>
          </div>

          {/* Synonyms */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>Synonyms</h2>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Synonyms (one group per line, format: group_name: synonym1, synonym2, ...)
              </label>
              <textarea
                value={synonymsInput}
                onChange={handleSynonymsChange}
                style={textareaStyle}
                placeholder="group1: synonym1, synonym2, synonym3&#10;group2: synonym4, synonym5..."
              />
            </div>
          </div>

          {/* Active Status */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>Status</h2>
            <div style={formGroupStyle}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  style={checkboxStyle}
                />
                Active
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              onClick={handleViewConfigs}
              style={{
                ...buttonStyle,
                backgroundColor: colors.background.secondary,
                color: colors.text.primary,
                border: `1px solid ${colors.borderVariants.light}`
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.borderVariants.light}
              onMouseLeave={(e) => e.target.style.backgroundColor = colors.background.secondary}
            >
              View All Configs
            </button>
            <button
              type="submit"
              style={buttonStyle}
              disabled={isSaving}
              onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = '#0056b3')}
              onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = colors.primary)}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </div>

      {isSigningOut && (
        <div style={loaderOverlayStyle}>
          <div style={loaderContainerStyle}>
            <div style={spinnerStyle}></div>
            <p style={loaderTextStyle}>Signing out...</p>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
      />

      {/* View Config Modal */}
      {isViewModalOpen && (
        <div style={loaderOverlayStyle}>
          <div style={{
            ...loaderContainerStyle,
            minWidth: '600px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: `1px solid ${colors.borderVariants.light}`,
              paddingBottom: '16px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: colors.text.primary,
                margin: 0
              }}>
                Current Configuration
              </h2>
              <button
                onClick={closeViewModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: colors.text.secondary,
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.target.style.color = colors.text.primary}
                onMouseLeave={(e) => e.target.style.color = colors.text.secondary}
              >
                ×
              </button>
            </div>

            {viewConfig && (
              <div style={{ color: colors.text.primary }}>
                {/* Tenant ID */}
                <div style={{ marginBottom: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Tenant ID:</strong>
                  <div style={{
                    padding: '12px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}>
                    {viewConfig.tenant_id}
                  </div>
                </div>

                {/* Prompt Templates */}
                <div style={{ marginBottom: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '12px', fontSize: '16px' }}>Prompt Templates:</strong>
                  <div style={{ paddingLeft: '16px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <strong style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>System Prompt:</strong>
                      <div style={{
                        padding: '12px',
                        backgroundColor: colors.background.secondary,
                        borderRadius: '6px',
                        fontSize: '13px',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '150px',
                        overflow: 'auto'
                      }}>
                        {viewConfig.prompt_templates?.system_prompt || '(empty)'}
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <strong style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>Query Reformulation Prompt:</strong>
                      <div style={{
                        padding: '12px',
                        backgroundColor: colors.background.secondary,
                        borderRadius: '6px',
                        fontSize: '13px',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '150px',
                        overflow: 'auto'
                      }}>
                        {viewConfig.prompt_templates?.query_reformulation_prompt || '(empty)'}
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <strong style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>Answer Grounding Instruction:</strong>
                      <div style={{
                        padding: '12px',
                        backgroundColor: colors.background.secondary,
                        borderRadius: '6px',
                        fontSize: '13px',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '150px',
                        overflow: 'auto'
                      }}>
                        {viewConfig.prompt_templates?.answer_grounding_instruction || '(empty)'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stopwords */}
                <div style={{ marginBottom: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Stopwords:</strong>
                  <div style={{
                    padding: '12px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    {viewConfig.stopwords && viewConfig.stopwords.length > 0
                      ? viewConfig.stopwords.join(', ')
                      : '(none)'}
                  </div>
                </div>

                {/* Human chat keywords */}
                <div style={{ marginBottom: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Human Chat Keywords:</strong>
                  <div style={{
                    padding: '12px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    {viewConfig.human_chat_keywords && viewConfig.human_chat_keywords.length > 0
                      ? viewConfig.human_chat_keywords.join(', ')
                      : '(none)'}
                  </div>
                </div>

                {/* Synonyms */}
                <div style={{ marginBottom: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Synonyms:</strong>
                  <div style={{
                    padding: '12px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    fontSize: '13px',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}>
                    {viewConfig.synonyms && Object.keys(viewConfig.synonyms).length > 0
                      ? Object.entries(viewConfig.synonyms).map(([key, values]) => (
                          <div key={key} style={{ marginBottom: '8px' }}>
                            <strong>{key}:</strong> {Array.isArray(values) ? values.join(', ') : values}
                          </div>
                        ))
                      : '(none)'}
                  </div>
                </div>

                {/* Status */}
                <div style={{ marginBottom: '20px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Status:</strong>
                  <div style={{
                    padding: '12px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: viewConfig.is_active ? '#10b981' : '#ef4444'
                    }}></span>
                    {viewConfig.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>

                {/* Timestamps */}
                <div style={{ marginBottom: '20px', fontSize: '12px', color: colors.text.secondary }}>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Created:</strong> {viewConfig.created_at ? new Date(viewConfig.created_at).toLocaleString() : 'N/A'}
                  </div>
                  <div>
                    <strong>Updated:</strong> {viewConfig.updated_at ? new Date(viewConfig.updated_at).toLocaleString() : 'N/A'}
                  </div>
                </div>

                <button
                  onClick={closeViewModal}
                  style={{
                    ...buttonStyle,
                    width: '100%',
                    marginTop: '16px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AIConfiguration

